import {
  AlertCircle,
  Bot,
  DollarSign,
  Loader,
  Send,
  TrendingUp,
  User,
} from 'lucide-react';
import React from 'react';
import { apiClient } from '../../services/ApiClient';
import { Button } from './button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './card';
import { Input } from './input';
import { ScrollArea } from './scroll-area';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  type?: string;
  followUpQuestions?: string[];
  suggestions?: string[];
  data?: any;
  missingData?: Array<{
    assetId: string;
    assetName: string;
    field: string;
    label: string;
    type: string;
    placeholder: string;
    question: string;
  }>;
}

// Helper to ensure content is always a string
function ensureStringContent(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value === undefined || value === null) return '';
  // Warn if not a string
  console.warn('[CopilotChat] Non-string message content:', value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export interface CopilotChatRef {
  sendMessage: (content: string) => void;
}

export const CopilotChat = React.forwardRef<
  CopilotChatRef,
  {
    className?: string;
    context?: {
      type: 'dashboard' | 'property';
      assetId?: string;
      assetName?: string;
    };
    hideHeader?: boolean;
  }
>(({ className, context, hideHeader = false }, ref) => {
  console.log('[CopilotChat] context prop:', context);
  console.log(
    '[CopilotChat] assetId type:',
    typeof context?.assetId,
    'value:',
    context?.assetId,
  );

  const getInitialGreeting = () => {
    if (context?.type === 'property' && context.assetName) {
      return {
        content: `Hello! I'm your AI assistant for "${context.assetName}". I can help you analyze this property's performance, calculate ROI, cash flow, and provide insights specific to this investment. What would you like to know about this property?`,
        followUpQuestions: [
          `What's the ROI for ${context.assetName}?`,
          'How is the cash flow for this property?',
          `What's the cap rate for ${context.assetName}?`,
          'How does this property compare to others?',
        ],
      };
    }

    return {
      content:
        "Hello! I'm your advanced BetterCapital AI assistant. I can help you with detailed financial analysis, ROI calculations, cash flow analysis, and portfolio performance insights. What would you like to know about your real estate investments?",
      followUpQuestions: [
        "What's my current ROI across all properties?",
        'How is my cash flow performing?',
        'Which properties are performing best?',
        "What's my portfolio's cap rate?",
      ],
    };
  };

  const initialGreeting = getInitialGreeting();

  const [messages, setMessages] = React.useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: initialGreeting.content,
      timestamp: new Date(),
      type: 'greeting',
      followUpQuestions: initialGreeting.followUpQuestions,
    },
  ]);
  const [isLoading, setIsLoading] = React.useState(false);
  const scrollAreaRef = React.useRef<HTMLDivElement>(null);

  // Improved scroll to bottom function
  const scrollToBottom = React.useCallback(() => {
    if (scrollAreaRef.current) {
      // Find the viewport element within the ScrollArea
      const viewport = scrollAreaRef.current.querySelector(
        '[data-radix-scroll-area-viewport]',
      );
      if (viewport) {
        viewport.scrollTop = viewport.scrollHeight;
      } else {
        // Fallback to the scroll area itself
        scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
      }
    }
  }, []);

  const sendMessage = async (content: string) => {
    if (!content.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: ensureStringContent(content),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    // Scroll immediately when user sends message
    setTimeout(scrollToBottom, 50);

    try {
      console.log('[CopilotChat] Sending request with context:', {
        type: context?.type,
        assetId: context?.assetId,
        assetName: context?.assetName,
      });

      const response = await apiClient.post('/copilot/query', {
        query: content,
        context: 'BetterCapital financial management system',
        assetContext:
          context?.type === 'property'
            ? {
                assetId: context.assetId,
                assetName: context.assetName,
                focus: 'property',
              }
            : {
                focus: 'portfolio',
              },
      });

      const data = response.data;

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content:
          ensureStringContent(data.answer) ||
          "I'm sorry, I couldn't process your request. Please try again.",
        timestamp: new Date(),
        type: data.type,
        followUpQuestions: data.followUpQuestions,
        suggestions: data.suggestions,
        data: data.data,
        missingData: data.missingData || data.missing_data,
      };

      setMessages((prev) => [...prev, assistantMessage]);
      // Scroll when assistant responds
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: ensureStringContent(
          `I'm sorry, there was an error processing your request: ${error instanceof Error ? error.message : 'Unknown error'}. Please make sure the backend server is running and you're authenticated.`,
        ),
        timestamp: new Date(),
        type: 'error',
      };
      setMessages((prev) => [...prev, errorMessage]);
      // Scroll when error message is added
      setTimeout(scrollToBottom, 100);
    } finally {
      setIsLoading(false);
    }
  };

  // Expose sendMessage function via ref
  React.useImperativeHandle(ref, () => ({
    sendMessage,
  }));

  const handleFollowUpQuestion = (question: string) => {
    sendMessage(question);
  };

  const getMessageIcon = (type?: string) => {
    switch (type) {
      case 'roi_analysis':
        return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'cash_flow_analysis':
        return <DollarSign className="h-4 w-4 text-blue-600" />;
      case 'missing_data':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Bot className="h-4 w-4 text-primary-foreground" />;
    }
  };

  const formatMessageContent = (content: string) => {
    if (typeof content !== 'string') {
      // Defensive: if content is an object, render as JSON or show a warning
      return (
        <div className="text-red-600 text-xs">
          [Invalid message content: {JSON.stringify(content)}]
        </div>
      );
    }
    // Convert markdown-style formatting to JSX
    return content.split('\n').map((line, index) => {
      if (
        line.startsWith('📊') ||
        line.startsWith('💰') ||
        line.startsWith('💡') ||
        line.startsWith('💵') ||
        line.startsWith('📈') ||
        line.startsWith('🏆') ||
        line.startsWith('🎯') ||
        line.startsWith('🔍') ||
        line.startsWith('🎉') ||
        line.startsWith('✅') ||
        line.startsWith('⚠️') ||
        line.startsWith('🌟') ||
        line.startsWith('⭐') ||
        line.startsWith('📈') ||
        line.startsWith('🏠')
      ) {
        return (
          <div key={index} className="font-semibold text-primary mb-1">
            {line}
          </div>
        );
      }
      if (line.startsWith('•')) {
        return (
          <div key={index} className="ml-4 text-sm">
            {line}
          </div>
        );
      }
      if (line.trim() === '') {
        return <div key={index} className="h-2" />;
      }
      return <div key={index}>{line}</div>;
    });
  };

  React.useEffect(() => {
    // Use setTimeout to ensure DOM is updated
    const timeoutId = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timeoutId);
  }, [scrollToBottom]);

  // Additional effect to scroll when loading state changes
  React.useEffect(() => {
    if (isLoading) {
      setTimeout(scrollToBottom, 50);
    }
  }, [isLoading, scrollToBottom]);

  // Helper component for rendering messages
  const ChatMessage = ({ message }: { message: Message }) => {
    const [inputValues, setInputValues] = React.useState<
      Record<string, string>
    >({});
    const [savingStates, setSavingStates] = React.useState<
      Record<string, boolean>
    >({});

    const handleMissingDataInput = async (
      assetId: string,
      field: string,
      value: number,
    ) => {
      try {
        setSavingStates((prev) => ({ ...prev, [field]: true }));

        const response = await apiClient.post('/copilot/update-missing-data', {
          assetId,
          field,
          value,
        });

        if (response.data.success) {
          // Refresh the analysis with the new data
          const analysisResponse = await apiClient.post('/copilot/query', {
            query: 'Recalculate my ROI with the updated data',
            context: 'BetterCapital financial management system',
            assetContext:
              context?.type === 'property'
                ? {
                    assetId: context.assetId,
                    assetName: context.assetName,
                    focus: 'property',
                  }
                : {
                    focus: 'portfolio',
                  },
          });

          const assistantMessage: Message = {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: ensureStringContent(analysisResponse.data.answer),
            timestamp: new Date(),
            type: analysisResponse.data.type,
            followUpQuestions: analysisResponse.data.followUpQuestions,
            suggestions: analysisResponse.data.suggestions,
            data: analysisResponse.data.data,
            missingData:
              analysisResponse.data.missingData ||
              analysisResponse.data.missing_data,
          };

          setMessages((prev) => [...prev, assistantMessage]);

          // Clear the input value after successful save
          setInputValues((prev) => ({ ...prev, [field]: '' }));
        }
      } catch (error) {
        console.error('Error updating missing data:', error);
      } finally {
        setSavingStates((prev) => ({ ...prev, [field]: false }));
      }
    };

    const handleSaveClick = (item: any) => {
      const value = Number.parseFloat(inputValues[item.field] || '0');
      if (!Number.isNaN(value) && item.assetId) {
        handleMissingDataInput(item.assetId, item.field, value);
      }
    };

    return (
      <div
        className={`flex gap-3 ${
          message.role === 'user' ? 'justify-end' : 'justify-start'
        }`}
      >
        {message.role === 'assistant' && (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary">
            {getMessageIcon(message.type)}
          </div>
        )}
        <div
          className={`max-w-[80%] rounded-lg px-4 py-2 ${
            message.role === 'user'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted'
          }`}
        >
          <div className="text-sm whitespace-pre-line">
            {formatMessageContent(message.content)}
          </div>

          {/* Missing Data Input */}
          {message.missingData && message.missingData.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="text-xs font-medium text-muted-foreground mb-2">
                Please provide missing data:
              </div>
              <div className="space-y-3">
                {message.missingData.map((item, index) => (
                  <div key={index} className="space-y-2">
                    <div className="text-xs font-medium">
                      {ensureStringContent(item.label)}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder={ensureStringContent(item.placeholder)}
                        className="text-xs h-8"
                        value={
                          inputValues[ensureStringContent(item.field)] || ''
                        }
                        onChange={(e) => {
                          setInputValues((prev) => ({
                            ...prev,
                            [ensureStringContent(item.field)]: e.target.value,
                          }));
                        }}
                        disabled={savingStates[ensureStringContent(item.field)]}
                      />
                      <Button
                        size="sm"
                        className="h-8 px-3 text-xs"
                        onClick={() => handleSaveClick(item)}
                        disabled={
                          savingStates[ensureStringContent(item.field)] ||
                          !inputValues[ensureStringContent(item.field)]
                        }
                      >
                        {savingStates[ensureStringContent(item.field)] ? (
                          <Loader className="h-3 w-3 animate-spin" />
                        ) : (
                          'Save'
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Follow-up Questions */}
          {message.followUpQuestions &&
            message.followUpQuestions.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border">
                <div className="text-xs font-medium text-muted-foreground mb-2">
                  Quick questions:
                </div>
                <div className="space-y-1">
                  {message.followUpQuestions.map((question, index) => (
                    <Button
                      key={index}
                      onClick={() =>
                        handleFollowUpQuestion(ensureStringContent(question))
                      }
                      variant="ghost"
                      size="sm"
                      className="block w-full text-left text-xs h-auto p-2 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 whitespace-normal"
                    >
                      {ensureStringContent(question)}
                    </Button>
                  ))}
                </div>
              </div>
            )}

          {/* Suggestions */}
          {message.suggestions && message.suggestions.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="text-xs font-medium text-muted-foreground mb-2">
                I can help you with:
              </div>
              <div className="space-y-1">
                {message.suggestions.map((suggestion, index) => (
                  <div key={index} className="text-xs text-muted-foreground">
                    • {ensureStringContent(suggestion)}
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs opacity-70 mt-1">
            {message.timestamp.toLocaleTimeString()}
          </p>
        </div>
        {message.role === 'user' && (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
            <User className="h-4 w-4" />
          </div>
        )}
      </div>
    );
  };

  // Helper component for chat input
  const ChatInput = ({
    onSendMessage,
    disabled,
    placeholder = 'Ask about your portfolio...',
  }: {
    onSendMessage: (message: string) => void;
    disabled: boolean;
    placeholder?: string;
  }) => {
    const [inputValue, setInputValue] = React.useState('');

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (inputValue.trim() && !disabled) {
        onSendMessage(inputValue);
        setInputValue('');
      }
    };

    return (
      <form onSubmit={handleSubmit} className="flex gap-2">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1"
        />
        <Button type="submit" disabled={disabled || !inputValue.trim()}>
          <Send className="h-4 w-4" />
        </Button>
      </form>
    );
  };

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Header */}
      {!hideHeader && (
        <div className="flex-shrink-0 p-4 border-b">
          <div className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            <h3 className="font-semibold">
              {context?.type === 'property' && context.assetName
                ? `${context.assetName} Assistant`
                : 'BetterCapital AI'}
            </h3>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {context?.type === 'property' && context.assetName
              ? `Get insights about ${context.assetName}`
              : 'Get detailed financial analysis, ROI calculations, and portfolio insights'}
          </p>
        </div>
      )}

      {/* Messages Area - Fixed height container */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <ScrollArea ref={scrollAreaRef} className="h-full p-4">
          <div className="space-y-4 pb-4">
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}
            {isLoading && (
              <div className="flex gap-3 justify-start">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary">
                  <Bot className="h-4 w-4 text-primary-foreground" />
                </div>
                <div className="bg-muted rounded-lg px-4 py-2">
                  <div className="flex space-x-1">
                    <div className="h-2 w-2 bg-current rounded-full animate-bounce" />
                    <div className="h-2 w-2 bg-current rounded-full animate-bounce [animation-delay:0.1s]" />
                    <div className="h-2 w-2 bg-current rounded-full animate-bounce [animation-delay:0.2s]" />
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Input Area */}
      <div className="flex-shrink-0 p-4 border-t">
        <ChatInput onSendMessage={sendMessage} disabled={isLoading} />
      </div>
    </div>
  );
});
